import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Bell, AlertCircle, TrendingUp, Calendar, Target, DollarSign } from 'lucide-react';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SmartAlerts({ user, onLogout }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'bill_due': return <Calendar className="w-5 h-5" />;
      case 'budget_alert': return <DollarSign className="w-5 h-5" />;
      case 'goal_milestone': return <Target className="w-5 h-5" />;
      case 'unusual_spending': return <TrendingUp className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const getAlertColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-900/20';
      case 'medium': return 'border-amber-400 bg-amber-50 dark:border-amber-600 dark:bg-amber-900/20';
      default: return 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20';
    }
  };

  const getIconColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 dark:text-red-400';
      case 'medium': return 'text-amber-600 dark:text-amber-400';
      default: return 'text-blue-600 dark:text-blue-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <Navigation user={user} onLogout={onLogout} currentPage="/alerts" />
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 dark:border-teal-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading alerts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/alerts" />

        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Smart Alerts</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Stay informed about your financial activities</p>
        </div>

        {alerts.length > 0 ? (
          <div className="space-y-4">
            {alerts.map((alert, index) => (
              <Card 
                key={index} 
                className={`glass p-6 border-l-4 ${getAlertColor(alert.priority)} dark:bg-gray-800`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-full ${getAlertColor(alert.priority)} flex items-center justify-center ${getIconColor(alert.priority)}`}>
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{alert.title}</h3>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        alert.priority === 'high' ? 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-300' :
                        alert.priority === 'medium' ? 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-300' :
                        'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                      }`}>
                        {alert.priority.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{alert.message}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      {new Date(alert.date).toLocaleDateString()} {new Date(alert.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass p-12 text-center dark:bg-gray-800">
            <Bell className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">No Alerts</h3>
            <p className="text-gray-600 dark:text-gray-400">You're all caught up! No alerts at this time.</p>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
}
